import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FC, useState } from 'react';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { Loader } from 'src/components/Loader';
import { toNoonUTC } from '@libs/frontend-services';

interface Props {
  orderId: string;
  open: boolean;
  onClose: () => void;
}

export const PickupRequestModal: FC<Props> = ({ orderId, open, onClose }) => {
  const [date, setDate] = useState<Date>();
  const utils = trpc.useUtils();
  const { errorToast } = useToast();

  const { mutate, isPending } = trpc.admin.order.adminRequestPickup.useMutation({
    onSuccess: () => {
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
      onClose();
    },
    onError: (error) => {
      errorToast({ description: error.message });
    },
  });

  const handleSubmit = () => {
    if (!date) return;

    mutate({
      orderId,
      pickupWindow: {
        day: toNoonUTC(date), // Normalize to noon UTC for consistent timezone handling
        start: 6, // Default to 6 AM
        end: 18, // Default to 6 PM
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setDate(undefined);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pickup Request</DialogTitle>
          <DialogDescription>
            Select a date for the pick-up. The customer will be notified once confirmed.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div className="border rounded-md p-4 flex flex-col items-center">
            <div className="w-[350px] min-h-[380px] flex flex-col items-center justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(newDate) => {
                  // Ensure we're working with a proper Date object
                  setDate(newDate ? new Date(newDate) : undefined);
                }}
                initialFocus
                fromDate={new Date()}
              />
              <div className="mt-4 text-sm text-muted-foreground">
                {date ? (
                  <span>Selected: {format(date, 'EEE, MMM dd, yyyy')}</span>
                ) : (
                  <span>Please select a date</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!date || isPending}>
            <div className="flex items-center gap-2">
              {isPending ? <Loader h={4} w={4} color="text-white" /> : null}
              Confirm
            </div>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
